/**
 * Deterministic plan color system.
 *
 * Known plans (free/pro/max) always get their canonical colors.
 * Unknown slugs get a color from the palette via slug hash — so the same
 * slug always produces the same color, no matter the order plans are loaded.
 */

interface PlanColorTokens {
  badge:  string;  // badge bg + text + border
  ring:   string;  // card ring border
  label:  string;  // text color
  check:  string;  // checkmark / indicator bg
  accent: string;  // gradient string (for card top bar)
  glow:   string;  // shadow color class
  icon:   string;  // icon container bg + text
  name:   string;  // plan name text color
}

const PALETTE: PlanColorTokens[] = [
  // 0 — slate (free)
  { badge: "bg-muted text-muted-foreground border-border",
    ring: "border-border", label: "text-foreground", check: "bg-primary",
    accent: "from-slate-400 to-slate-500", glow: "shadow-slate-500/10",
    icon: "bg-slate-500/10 text-slate-400", name: "text-slate-400" },
  // 1 — blue (pro)
  { badge: "bg-blue-500/10 text-blue-500 border-blue-500/20",
    ring: "border-blue-500/40", label: "text-blue-500", check: "bg-blue-500",
    accent: "from-blue-500 to-blue-600", glow: "shadow-blue-500/15",
    icon: "bg-blue-500/10 text-blue-400", name: "text-blue-400" },
  // 2 — purple (max)
  { badge: "bg-purple-500/10 text-purple-500 border-purple-500/20",
    ring: "border-purple-500/40", label: "text-purple-500", check: "bg-purple-500",
    accent: "from-violet-500 via-purple-500 to-pink-500", glow: "shadow-purple-500/20",
    icon: "bg-purple-500/10 text-purple-400", name: "text-purple-400" },
  // 3 — amber
  { badge: "bg-amber-500/10 text-amber-600 border-amber-500/20",
    ring: "border-amber-500/40", label: "text-amber-600", check: "bg-amber-500",
    accent: "from-amber-400 to-amber-500", glow: "shadow-amber-500/15",
    icon: "bg-amber-500/10 text-amber-400", name: "text-amber-400" },
  // 4 — emerald
  { badge: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
    ring: "border-emerald-500/40", label: "text-emerald-600", check: "bg-emerald-500",
    accent: "from-emerald-500 to-teal-500", glow: "shadow-emerald-500/15",
    icon: "bg-emerald-500/10 text-emerald-400", name: "text-emerald-400" },
  // 5 — rose
  { badge: "bg-rose-500/10 text-rose-500 border-rose-500/20",
    ring: "border-rose-500/40", label: "text-rose-500", check: "bg-rose-500",
    accent: "from-rose-500 to-pink-500", glow: "shadow-rose-500/15",
    icon: "bg-rose-500/10 text-rose-400", name: "text-rose-400" },
  // 6 — sky
  { badge: "bg-sky-500/10 text-sky-500 border-sky-500/20",
    ring: "border-sky-500/40", label: "text-sky-500", check: "bg-sky-500",
    accent: "from-sky-400 to-cyan-500", glow: "shadow-sky-500/15",
    icon: "bg-sky-500/10 text-sky-400", name: "text-sky-400" },
  // 7 — orange
  { badge: "bg-orange-500/10 text-orange-600 border-orange-500/20",
    ring: "border-orange-500/40", label: "text-orange-600", check: "bg-orange-500",
    accent: "from-orange-400 to-red-500", glow: "shadow-orange-500/15",
    icon: "bg-orange-500/10 text-orange-400", name: "text-orange-400" },
];

// Known plan slugs always map to these fixed palette indices
const FIXED: Record<string, number> = { free: 0, pro: 1, max: 2 };
const DYNAMIC_START = 3;

function slugToIndex(slug: string): number {
  if (slug in FIXED) return FIXED[slug];
  const hash = [...slug].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return DYNAMIC_START + (hash % (PALETTE.length - DYNAMIC_START));
}

export function getPlanColors(slug: string): PlanColorTokens {
  return PALETTE[slugToIndex(slug)];
}
